import type { NotificationConditionType } from '@cims-online/shared'
import { Form } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import type {
  NotificationField,
  NotificationStatus,
} from '../../../../../types'
import { approvalFieldSettings } from '../../../../../utils/approvalFieldSettings'
import { useStatusOptions } from '../../../hooks/useStatusOptions'
import { createNotificationSystemStateReference } from '../../../utils/referenceValues'
import type { NotificationFormValues } from '../../../types'

type ConditionTypeSettingsProps = {
  conditionName: number
  fields: NotificationField[]
  statuses: NotificationStatus[]
  type: NotificationConditionType['type']
}

/**
 * Renders the fields for a selected notification condition type.
 *
 * This module must be rendered within the notification form. The condition
 * type selector stores the selected approvals field as a separate form draft.
 * Inactive fields and options remain visible when already selected but are not
 * offered as new condition settings.
 */
function ConditionTypeSettings({
  conditionName,
  fields,
  statuses,
  type,
}: ConditionTypeSettingsProps) {
  const form = Form.useFormInstance<NotificationFormValues>()
  const conditions = Form.useWatch('conditions', form)
  const fieldValue = conditions?.[conditionName]?.field
  const fieldId = fieldValue?.value.toString()
  const field = fieldId ? fields.find(({ id }) => id === fieldId) : undefined
  const { plural, singular } = approvalFieldSettings(field)
  const approvalOptions = useOptions({
    items:
      field?.settings.type === 'approvals' && !field.deleted
        ? field.settings.options.filter(({ deleted }) => !deleted)
        : [],
    label: 'label',
    sort: 'label',
    tooltip: 'tooltip',
    value: 'id',
  })
  const customStatusOptions = useStatusOptions(statuses)
  const statusOptions = [
    createNotificationSystemStateReference('draft'),
    ...customStatusOptions,
    createNotificationSystemStateReference('closed'),
  ]

  if (type === 'status_includes')
    return (
      <CIMSFormItem
        label="Statuses"
        name={[conditionName, 'statuses']}
        requiredMessage="Please select at least one status"
      >
        <CIMSSelect
          mode="multiple"
          options={statusOptions}
          placeholder="Select statuses"
        />
      </CIMSFormItem>
    )

  if (type !== 'includes_options') return null

  return (
    <CIMSFormItem
      label={plural}
      name={[conditionName, 'options']}
      requiredMessage={`Please select at least one ${singular.toLowerCase()}`}
    >
      <CIMSSelect
        disabled={!field}
        mode="multiple"
        options={approvalOptions}
        placeholder={`Select ${plural.toLowerCase()}`}
      />
    </CIMSFormItem>
  )
}

export default ConditionTypeSettings
